import { computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { TxfJobStore } from './TxfJobStore'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type TxfJob from '@/features/jobs/details/tax-type/txf/TxfJob'
import { FeatureToggles } from '@/features/feature-toggles'
import { links } from '@/utils/link'
import type { GetExternalUrlDelegate } from '@/features/shell/platform/types'

export class TxfJobViewModel implements ActivationLifecycle {
  /**
   * Manages the ViewModel's lifecycle, ensuring proper cleanup and activation.
   */
  private readonly _lifecycle = new LifecycleTracker()

  @observable accessor txfJob: TxfJob | null = null

  constructor(
    private readonly _getExternalUrlDelegate: GetExternalUrlDelegate,
    public readonly job: SubmittedJob,
    private readonly _txfJobStore: TxfJobStore,
  ) {}

  @computed
  get isEnabled(): boolean {
    return FeatureToggles.txfIntegration()
  }

  @computed
  get isReadyForOnboarding(): boolean {
    return (
      this.isEnabled &&
      this.job.status !== 'UnderConstruction' &&
      this.job.status !== 'InfoGathering' &&
      this.txfJob?.isReadyToOnboard === true
    )
  }

  fetchTxfJob = task(async (signal?: AbortSignal) => {
    if (!this.isEnabled) {
      return
    }

    const txfJob = await this._txfJobStore.fetchTxfJob(this.job.id, signal)

    signal?.throwIfAborted()

    runInAction(() => {
      this.txfJob = txfJob
    })
  })

  viewInTxf() {
    const handle = this._getExternalUrlDelegate().createHandle({
      sameWindow: true,
    })
    handle.open(links.taxPrepOrganizer(this.job.id))
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }
}
